import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { HttpService } from '../http/http.service';
import { AuthService } from '../auth/auth.service';

export interface Tenant {
  tenantId: string;
  customerName: string | null;
  accessLevel: 'READER' | 'WRITER' | 'ADMIN';
}

export interface TenantsResponse {
  userId: string;
  tenants: Tenant[];
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private _selectedTenantId: string | null = null;
  private _selectedCustomerName: string | null = null;
  private _selectedTenantSessionId: string | null = null;
  private readonly _selectedTenant$ = new Subject<string>();

  /**
   * Emits the new tenant id whenever the selection actually changes. Tenant-scoped state elsewhere
   * (feature flags above all) subscribes to this instead of every switcher having to remember to
   * refresh it. Restoring the tenant from localStorage on startup is not a change and does not emit.
   */
  readonly selectedTenant$ = this._selectedTenant$.asObservable();

  constructor(
    private readonly _httpService: HttpService,
    private readonly _authService: AuthService
  ) {
    this._selectedTenantId = localStorage.getItem('selectedTenantId');
    this._selectedCustomerName = localStorage.getItem('selectedCustomerName');
    this._selectedTenantSessionId = localStorage.getItem('selectedTenantSessionId');
  }

  async loadTenants(): Promise<TenantsResponse> {
    const response = await firstValueFrom(this.getTenants());
    return response;
  }

  getTenants(): Observable<TenantsResponse> {
    return this._httpService.createHttpClient().url('/authz/api/v1/authorization/tenants').get<TenantsResponse>();
  }

  setSelectedTenant(tenantId: string, customerName?: string | null): void {
    const isTenantChange = this._selectedTenantId !== tenantId;

    this._selectedTenantId = tenantId;
    localStorage.setItem('selectedTenantId', tenantId);
    this.rememberOwningSession();

    if (customerName && customerName.trim()) {
      this._selectedCustomerName = customerName;
      localStorage.setItem('selectedCustomerName', customerName);
    } else {
      this._selectedCustomerName = null;
      localStorage.removeItem('selectedCustomerName');
    }

    // Emitted last so subscribers observe the fully settled selection, never a half-written one.
    if (isTenantChange) {
      this._selectedTenant$.next(tenantId);
    }
  }

  /**
   * The tenant picked during the login session that is currently active. A choice is deliberately
   * not carried across logins, so logging out and back in asks again while a refresh does not.
   * Unlike getSelectedTenant() it never falls back to the JWT tenant: callers deciding whether to
   * prompt must tell "the user already chose" apart from "we defaulted to the token".
   */
  getTenantSelectedThisSession(): string | null {
    if (!this._selectedTenantId || !this.isOwnedByCurrentSession()) {
      return null;
    }

    return this._selectedTenantId;
  }

  getSelectedTenant(): string | null {
    // Return selected tenant from localStorage if available,
    // otherwise fall back to tenant from JWT token
    return this._selectedTenantId || this._authService.getTenantFromToken() || null;
  }

  getSelectedCustomerName(): string | null {
    return this._selectedCustomerName;
  }

  /** A missing session id proves nothing, so the stored choice is treated as stale rather than reused. */
  private isOwnedByCurrentSession(): boolean {
    const currentSessionId = this._authService.getSessionId();

    return !!currentSessionId && this._selectedTenantSessionId === currentSessionId;
  }

  private rememberOwningSession(): void {
    this._selectedTenantSessionId = this._authService.getSessionId() ?? null;

    if (this._selectedTenantSessionId) {
      localStorage.setItem('selectedTenantSessionId', this._selectedTenantSessionId);
    } else {
      localStorage.removeItem('selectedTenantSessionId');
    }
  }
}
